import { DateTime } from 'luxon'
import { PromoCodeValidationError } from 'types/uscTypes'

/**
 * Maps a rejection from the voucher flow to a translation key.
 *
 * Returns static literal keys so every key stays greppable for the translation
 * tooling — never build a key by interpolating the reason.
 *
 * A thrown `Error` means the request itself failed (offline, 5xx); anything else
 * is a validation reason from the backend.
 */
export const promoCodeErrorKey = (reason: PromoCodeValidationError | Error): string => {
  if (reason instanceof globalThis.Error) {
    return 'usc.errors.network'
  }

  switch (reason) {
    case 'wrong_partner':
      return 'usc.errors.wrongPartner'
    case 'inactive':
    case 'expired':
      return 'usc.errors.expired'
    case 'limit_reached':
      return 'usc.errors.limitReached'
    case 'already_redeemed':
      return 'usc.errors.alreadyRedeemed'
    case 'already_has_active_voucher':
      return 'usc.errors.alreadyHasActiveVoucher'
    default:
      return 'usc.errors.invalidCode'
  }
}

export const authErrorKey = (error: { code?: string } | Error): string => {
  switch ((error as { code?: string }).code) {
    case 'auth/email-already-in-use':
      return 'usc.errors.emailInUse'
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'usc.errors.wrongCredentials'
    case 'auth/invalid-email':
      return 'FirebaseError.auth/invalid-email'
    case 'auth/weak-password':
      return 'FirebaseError.auth/weak-password'
    case 'auth/too-many-requests':
      return 'FirebaseError.auth/too-many-requests'
    default:
      return 'usc.errors.network'
  }
}

/**
 * Formats the `freeSubscription` expiry for display, in the member's locale.
 *
 * Returns null for anything that is not a 'yyyy-MM-dd' date ('active', 'available',
 * empty, malformed) — the caller then shows no date rather than inventing one.
 */
export const formatFreeSubscriptionUntil = (freeSubscription: string, locale: string): string | null => {
  const parsed = DateTime.fromFormat(freeSubscription, 'yyyy-MM-dd')
  if (!parsed.isValid) {
    return null
  }
  return parsed.setLocale(locale).toLocaleString({ day: 'numeric', month: 'short', year: 'numeric' })
}
